import React, { FC, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { useAbortController } from '@packages/core/http';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import {
    cvtDiagnosticUploadsActions,
    isCvtDiagnosticEditable,
    isUploadedCvtDiagnosticProcessing,
    useCvtDiagnosticUploadsDispatch,
    useCvtDiagnosticUploadsState,
} from '@packages/contexts/qmr-cvt-diagnostic';
import {
    useQmrState,
    saveQmr,
    useQmrDispatch,
    useQmrReportSections,
    QmrReportSectionStatus,
    patchQmr,
    saveQmrCvtChallengeDetails,
    updateQmrDtcCodes,
    qmrActions,
} from '@packages/contexts/qmrs';
import { PatchQmrDto, QmrStatusId, UpdateQmrCvtChallengeDetailsDto } from '@packages/models/api';
import { Icon, Button, TextBadge, Typography } from '@packages/ui/shared';

import { analyticsService, qmrsService } from '@web/services/singletons';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics';
import { useGoogleAnalytics } from '@web/core/google-analytics/use-google-analytics';
import QmrActions from './qmr-actions';

const qmrHeaderStyles = createUseStyles({
    modalContainer: {
        display: 'flex',
    },
    modalIcon: {
        flexBasis: '17%',
    },
});

interface Props {
    setShowSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    setQmrSuccessfullyEscalated: React.Dispatch<React.SetStateAction<boolean>>;
    setQmrAddedToInvestigationSuccessfully: React.Dispatch<React.SetStateAction<string>>;
}

const QmrHeader: FC<Props> = ({
    setShowSubmitted,
    setQmrSuccessfullyEscalated,
    setQmrAddedToInvestigationSuccessfully,
}) => {
    const classes = qmrHeaderStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isSubmitting, isUpdatingCvtDiagnostic, localPatch, qmr, submitAttempted } = useQmrState();
    const { googleAnalyticsService } = useGoogleAnalytics();
    const { isNqrCvtDiagnosticsFeatureEnabled } = useFeatureFlags();
    const qmrDispatch = useQmrDispatch();
    const cvtDiagnosticUploads = useCvtDiagnosticUploadsState();
    const cvtDiagnosticUploadsDispatch = useCvtDiagnosticUploadsDispatch();
    const editRouteMatch = useMatch(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}/:displayIdentifier/edit`);
    const toughBookAttachmentsRoute = '/administration/toughbook-attachments';
    const reportSections = useQmrReportSections(submitAttempted, isNqrCvtDiagnosticsFeatureEnabled);
    const { abortSignalRef } = useAbortController();
    const [isDeleting, setIsDeleting] = useState(false);
    const isDraft = qmr?.qmrStatus.qmrStatusId === QmrStatusId.Draft;
    const [noAttachmentsAdded, setNoAttachmentsAdded] = useState(false);
    const [showCvtDiagnosticProcessingModal, setShowCvtDiagnosticProcessingModal] = useState<boolean>(false);

    const canSubmitQmr =
        !isSubmitting &&
        Object.values(reportSections).every((section) => {
            return (
                !!section &&
                (section.status === QmrReportSectionStatus.Completed ||
                    section.status === QmrReportSectionStatus.Optional)
            );
        }) &&
        (!!editRouteMatch ? !!localPatch : true);

    /**
     * Whether the Submit button is disabled. An update to the CVT diagnostic holds it without changing
     * `canSubmitQmr`, whose changes the validation analytics log.
     */
    const isSubmitDisabled = !canSubmitQmr || isUpdatingCvtDiagnostic;

    const uploadedDiagnostic = qmr ? cvtDiagnosticUploads[qmr.qmrId] : undefined;

    /** Whether the uploaded diagnostic shows as processing in the report's CVT section. */
    const isUploadedDiagnosticProcessing =
        isCvtDiagnosticEditable({ isEditRoute: Boolean(editRouteMatch), isNqrCvtDiagnosticsFeatureEnabled, qmr }) &&
        isUploadedCvtDiagnosticProcessing({ qmr, uploadedDiagnostic });

    const hasAttachedDiagnostic = Boolean(qmr?.cvtDiagnosticsJobId);

    const exitPage = () => {
        if (editRouteMatch) {
            navigate(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}`, {
                state: location.state,
            });
        } else if (location.state?.from === toughBookAttachmentsRoute) {
            navigate(toughBookAttachmentsRoute, {
                state: location.state,
            });
        } else {
            const historyFromPrevTab = location.search;
            navigate({
                pathname: `/${qmr?.isNqr ? 'nqrs' : 'qmrs'}`,
                search: location.state?.listQueryParams
                    ? '?' + location.state.listQueryParams
                    : historyFromPrevTab
                      ? historyFromPrevTab
                      : undefined,
            });
        }
    };

    async function handleBackButtonClick() {
        const cvtChallengeDetails = localPatch?.cvtChallengeDetails;
        const isChallengeHeld = cvtChallengeDetails !== undefined;

        qmrDispatch(qmrActions.setIsLeaving(true));

        try {
            if (isDraft && isChallengeHeld) {
                const isHeldChallengeSaved = await saveHeldCvtChallengeDetails(cvtChallengeDetails);

                if (!isHeldChallengeSaved) {
                    return;
                }
            }

            exitPage();
        } catch (e) {
            qmrDispatch(qmrActions.setIsLeaving(false));
            alert(e.message);
        }
    }

    async function handleSaveAndExitButtonClick() {
        if (!qmr) {
            return;
        }

        qmrDispatch(qmrActions.setIsLeaving(true));

        try {
            const cvtChallengeDetails = localPatch?.cvtChallengeDetails;

            if (cvtChallengeDetails !== undefined) {
                const isHeldChallengeSaved = await saveHeldCvtChallengeDetails(cvtChallengeDetails);

                if (!isHeldChallengeSaved) {
                    return;
                }
            }

            await saveQmr({
                qmr,
                qmrsService,
                qmrDispatch,
                signal: abortSignalRef.current,
            });

            exitPage();
        } catch (e) {
            qmrDispatch(qmrActions.setIsLeaving(false));
            alert(e.message);
        }
    }
    async function handleSubmitQMR() {
        if (!qmr) {
            return;
        }

        if (!editRouteMatch) {
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_SUBMISSION_STARTED, { tsId: qmr.stsIdentifier });
        }

        qmrDispatch(qmrActions.setIsSubmitting(true));

        try {
            if (!!editRouteMatch && !!localPatch) {
                const { cvtChallengeDetails, dtcCodes, ...qmrPatch } = localPatch;

                if (!!dtcCodes) {
                    await updateQmrDtcCodes({
                        qmrId: qmr.qmrId,
                        qmrsService,
                        qmrDispatch,
                        signal: abortSignalRef.current,
                        dtcCodes: dtcCodes.map((c) => ({
                            dtcCodeId: c.dtcCodeId,
                            freezeFrameDataAvailable: !!c.freezeFrameDataAvailable,
                            markedAsPrimary: !!c.markedAsPrimary,
                        })),
                    });
                }

                if (cvtChallengeDetails !== undefined) {
                    const isHeldChallengeSaved = await saveHeldCvtChallengeDetails(cvtChallengeDetails);

                    if (!isHeldChallengeSaved) {
                        return;
                    }
                }

                await patchQmr({
                    qmrId: qmr.qmrId,
                    qmrsService,
                    qmrDispatch,
                    signal: abortSignalRef.current,
                    qmrPatch: {
                        ...qmrPatch,
                        qmrStatusId: qmr.isNqr ? qmr.qmrStatus.qmrStatusId : QmrStatusId.PendingReviewUpdated,
                        qmrSubmit: true,
                    },
                });
            } else {
                const { cvtChallengeDetails, ...qmrPatch }: PatchQmrDto = localPatch ?? {};

                if (cvtChallengeDetails !== undefined) {
                    const isHeldChallengeSaved = await saveHeldCvtChallengeDetails(cvtChallengeDetails);

                    if (!isHeldChallengeSaved) {
                        return;
                    }
                }

                const updatedQmr = await patchQmr({
                    qmrId: qmr.qmrId,
                    qmrsService,
                    qmrDispatch,
                    signal: abortSignalRef.current,
                    qmrPatch: {
                        ...qmrPatch,
                        qmrStatusId: QmrStatusId.PendingReview,
                        qmrSubmit: true,
                    },
                });

                if (!updatedQmr) {
                    return;
                }

                analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_SUBMISSION_SUCCEEDED, { tsId: qmr.stsIdentifier });

                googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrSubmit, {
                    success: true,
                    attachment_count: updatedQmr.attachmentCount,
                    dtc_count: updatedQmr.dtcCodes?.length ?? 0,
                    fail_code: updatedQmr.failCode,
                    report_type: updatedQmr.isNqr ? 'nqr' : 'qmr',
                });
            }

            cvtDiagnosticUploadsDispatch(cvtDiagnosticUploadsActions.clearUploadedDiagnostic({ qmrId: qmr.qmrId }));
            setShowSubmitted(true);
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_SUBMISSION_SUCCEEDED);
            setTimeout(() => {
                exitPage();
            }, 3000);
        } catch (e) {
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_SUBMISSION_FAILED, {
                report_type: qmr.isNqr ? 'NQR' : 'QMR',
                sts_id: qmr.stsIdentifier,
            });
            qmrDispatch(qmrActions.setIsSubmitting(false));
            alert(e.message);
            googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrSubmit, { success: false });
        }
    }

    /**
     * Saves the CVT challenge held in `localPatch`, or deletes the saved one when given `null`. It goes through its
     * own endpoint because the report's own saves do not include it.
     *
     * Resolves `false` when there is no report or the request was cancelled; a failed request throws.
     */
    async function saveHeldCvtChallengeDetails(
        cvtChallengeDetails: UpdateQmrCvtChallengeDetailsDto | null
    ): Promise<boolean> {
        if (!qmr?.qmrId) {
            return false;
        }

        const updatedQmr = await saveQmrCvtChallengeDetails({
            qmrId: qmr.qmrId,
            cvtChallengeDetails,
            qmrsService,
            qmrDispatch,
            signal: abortSignalRef.current,
        });

        return Boolean(updatedQmr);
    }

    /**
     * Opens the CVT diagnostic processing modal while the uploaded diagnostic is processing, so the user knows the new
     * test isn't included, and submits otherwise.
     */
    function promptOrSubmitQmr() {
        if (isUploadedDiagnosticProcessing) {
            setShowCvtDiagnosticProcessingModal(true);
            return;
        }

        handleSubmitQMR();
    }

    function handleCvtDiagnosticProcessingCancelButtonClick() {
        setShowCvtDiagnosticProcessingModal(false);
    }

    function handleCvtDiagnosticProcessingConfirmButtonClick() {
        setShowCvtDiagnosticProcessingModal(false);
        handleSubmitQMR();
    }

    /** Closes the CVT diagnostic processing modal once the test stops processing, so its message can't go stale. */
    useEffect(() => {
        if (isUploadedDiagnosticProcessing) {
            return;
        }

        setShowCvtDiagnosticProcessingModal(false);
    }, [isUploadedDiagnosticProcessing]);

    if (!qmr) {
        return null;
    }

    const handleSubmitButtonClick = () => {
        if (qmr?.assets?.length === 0) {
            setNoAttachmentsAdded(true);
        } else {
            setNoAttachmentsAdded(false);
            promptOrSubmitQmr();
        }
    };

    const handleValidationButtonClick = () => {
        if (!canSubmitQmr) {
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_VALIDATION_FAILED);
            googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrValidationFail);
        }
    };

    const handleDeleteQmr = async () => {
        try {
            const response = await qmrsService.deleteQmr(qmr.qmrId, abortSignalRef.current);

            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrDelete, {
                success: true,
                report_type: qmr.isNqr ? 'nqr' : 'qmr',
            });

            navigate(`/${qmr?.isNqr ? 'nqrs' : 'qmrs'}`, { replace: true });
        } catch (e) {
            googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrDelete, { success: false });
            alert(e.message);
        }
    };

    const handleCancelDeleteQmr = () => {
        setIsDeleting(false);
    };

    const handleSkipAttachments = () => {
        setNoAttachmentsAdded(false);
        promptOrSubmitQmr();
    };

    const handleAddAttachments = async () => {
        qmrDispatch(qmrActions.setIsLeaving(true));

        try {
            if (qmr && localPatch) {
                const { cvtChallengeDetails, dtcCodes, ...qmrPatch } = localPatch;

                if (Array.isArray(dtcCodes)) {
                    await updateQmrDtcCodes({
                        qmrId: qmr.qmrId,
                        qmrsService,
                        qmrDispatch,
                        signal: abortSignalRef.current,
                        dtcCodes: dtcCodes.map((c) => ({
                            dtcCodeId: c.dtcCodeId,
                            freezeFrameDataAvailable: !!c.freezeFrameDataAvailable,
                            markedAsPrimary: !!c.markedAsPrimary,
                        })),
                    });
                }

                if (cvtChallengeDetails !== undefined) {
                    const isHeldChallengeSaved = await saveHeldCvtChallengeDetails(cvtChallengeDetails);

                    if (!isHeldChallengeSaved) {
                        return;
                    }
                }

                if (Object.keys(qmrPatch).length > 0) {
                    await patchQmr({
                        qmrId: qmr.qmrId,
                        qmrsService,
                        qmrDispatch,
                        signal: abortSignalRef.current,
                        qmrPatch,
                    });
                }
            }
        } catch (e) {
            qmrDispatch(qmrActions.setIsLeaving(false));
            alert(e.message);
            return;
        }

        navigate(`/qmrs/${qmr?.displayIdentifier}#attachments`);
        setTimeout(() => {
            setNoAttachmentsAdded(false);
        }, 1000);
    };

    const prevValidation = useRef(canSubmitQmr);

    useEffect(() => {
        if (canSubmitQmr && !prevValidation.current) {
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_VALIDATION_PASSED);
        } else if (!canSubmitQmr && prevValidation.current) {
            analyticsService.logEvent(ANALYTICS_EVENTS.QMR_NQR_VALIDATION_FAILED);
            googleAnalyticsService.track(GoogleAnalyticsEvents.QmrNqrValidationFail);
        }
        prevValidation.current = canSubmitQmr;
    }, [canSubmitQmr]);

    return (
        <>
            <div className="d-flex align-items-center">
                <div className="mr-4">
                    <Button disabled={isUpdatingCvtDiagnostic} variant="link" onPress={handleBackButtonClick}>
                        <Icon name="arrow-back" />
                    </Button>
                </div>
                <div className="mb-0 mr-4">
                    <Typography variant="h5">{qmr?.displayIdentifier}</Typography>
                </div>

                <TextBadge>{qmr?.qmrStatus.description}</TextBadge>
            </div>
            <div className="d-flex align-items-center">
                {qmr?.qmrStatus?.qmrStatusId === 'DRAFT' && (
                    <Typography variant="labelRegular" color="textDarkSecondary">
                        Saved on {qmr.lastUpdatedTimestampDescription}
                    </Typography>
                )}

                {!editRouteMatch && isDraft && (
                    <div className="ml-6">
                        <Button
                            variant="secondary"
                            onPress={handleSaveAndExitButtonClick}
                            disabled={isUpdatingCvtDiagnostic}
                            isLoading={isSubmitting}
                            title={t('buttons:saveAndExit')}
                        />
                    </div>
                )}

                {(isDraft || editRouteMatch) && (
                    <div className="ml-4" onClick={handleValidationButtonClick}>
                        <Button
                            variant="primary"
                            onPress={handleSubmitButtonClick}
                            isLoading={isSubmitting}
                            disabled={isSubmitDisabled}
                            title={editRouteMatch ? t('buttons:saveChanges', 'Save Changes') : t('buttons:submit')}
                        />
                    </div>
                )}

                {qmr.capabilities.deleteQmr && isDraft && (
                    <div className="ml-4">
                        <Button
                            variant="ghost-gray"
                            onPress={() => {
                                setIsDeleting(true);
                            }}
                        >
                            <Icon name="trash" />
                        </Button>
                    </div>
                )}

                {!isDraft && !editRouteMatch && (
                    <QmrActions
                        onRestrictQmrSuccess={() => {}}
                        onUnrestrcitQmrSuccess={() => {}}
                        setQmrSuccessfullyEscalated={setQmrSuccessfullyEscalated}
                        setQmrAddedToInvestigationSuccessfully={setQmrAddedToInvestigationSuccessfully}
                        hideQMRId={true}
                    />
                )}

                <Modal show={isDeleting} onHide={handleCancelDeleteQmr}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            {t('modals:deleteQmrConfirm.title', {
                                qmrId: qmr.qmrId,
                                displayName: qmr.displayIdentifier,
                            })}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className={classes.modalContainer}>
                            <div className={classes.modalIcon}>
                                <Icon name="warning" />
                            </div>
                            <div>
                                <div>
                                    <p>
                                        <b>{t('modals:deleteQmrConfirm.subtitle')}</b>
                                    </p>
                                </div>
                                <div>
                                    <p>{t('modals:deleteQmrConfirm.body')}</p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onPress={handleCancelDeleteQmr} variant="ghost-blue">
                            {t('modals:deleteQmrConfirm.cancel')}
                        </Button>
                        <Button onPress={handleDeleteQmr} variant="danger">
                            {t('modals:deleteQmrConfirm.delete')}
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Modal show={noAttachmentsAdded}>
                    <Modal.Body>
                        <div className={classes.modalContainer}>
                            <div className={classes.modalIcon}>
                                <Icon name="info" />
                            </div>
                            <div>
                                <p>{t('modals:noAttachmentWarning.message')}</p>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onPress={handleSkipAttachments} variant="ghost-gray">
                            {t('modals:noAttachmentWarning.skip')}
                        </Button>
                        <Button disabled={isUpdatingCvtDiagnostic} onPress={handleAddAttachments} variant="primary">
                            {t('modals:noAttachmentWarning.add')}
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Modal show={showCvtDiagnosticProcessingModal}>
                    <Modal.Header>
                        <Modal.Title>
                            {t('modals:cvtDiagnosticProcessing.title', "The diagnostic test hasn't finished yet")}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className={classes.modalContainer}>
                            <div className={classes.modalIcon}>
                                <Icon color="yellowOne" name="warning" />
                            </div>
                            <Typography>
                                {t(
                                    hasAttachedDiagnostic
                                        ? 'modals:cvtDiagnosticProcessing.attached'
                                        : 'modals:cvtDiagnosticProcessing.unavailable',
                                    { diagnosticId: qmr.cvtDiagnosticsJobId }
                                )}
                            </Typography>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onPress={handleCvtDiagnosticProcessingCancelButtonClick} variant="ghost-blue">
                            {t('modals:cvtDiagnosticProcessing.buttons.cancel', 'Cancel')}
                        </Button>
                        <Button
                            disabled={isUpdatingCvtDiagnostic}
                            onPress={handleCvtDiagnosticProcessingConfirmButtonClick}
                            variant="primary"
                        >
                            {t(
                                editRouteMatch
                                    ? 'modals:cvtDiagnosticProcessing.buttons.saveChanges'
                                    : 'modals:cvtDiagnosticProcessing.buttons.submit'
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </>
    );
};

export default QmrHeader;
